/**
 * SimplePay test prompt of 3 November 2026, §5, §6 and §1: what the page
 * shows on the SimplePay route, before the start call.
 *
 * The data-transfer notice is SP doc §8
 * (`docs/reference/PaymentService_SimplePay_2.x_Payment_HU_260901.pdf`), word
 * for word, with the four brackets filled:
 *
 *   company and seat  Planteen Food Kft., 1031 Budapest, Záhony utca 7. — as
 *                     the ÁSZF §1 states them (docs/legal/…aszf_hu_20261013.md)
 *   web address       catering.planteen.hu — VERIFY with Barna that this is
 *                     the domain in the SimplePay contract
 *   data              generated from NOTICE_FIELDS, which names in words each
 *                     personal field the start call sends
 *                     (`START_PERSONAL_FIELDS` in
 *                     supabase/functions/_shared/payments/simplepay.ts).
 *                     `simplepayNotice.test.ts` fails if the two differ.
 */

export const MERCHANT_NAME = 'Planteen Food Kft.'
export const MERCHANT_SEAT = '1031 Budapest, Záhony utca 7.'
export const ACCEPTANCE_SITE = 'catering.planteen.hu'
export const SIMPLEPAY_PRIVACY_URL = 'https://simplepay.hu/adatkezelesi-tajekoztatok/'

/** SP doc §7: the payment information the logo links to, per language. */
export const SIMPLEPAY_INFO_PDF = {
  hu: 'https://simplepartner.hu/PaymentService/Fizetesi_tajekoztato.pdf',
  en: 'https://simplepartner.hu/PaymentService/Payment_information.pdf',
} as const

/**
 * Each personal field of the start call, in plain words. Fields that share a
 * phrase (the billing address's parts) are named once in the list.
 */
export const NOTICE_FIELDS: Readonly<Record<string, { hu: string; en: string }>> = {
  'invoice.name': { hu: 'név', en: 'name' },
  customerEmail: { hu: 'e-mail cím', en: 'email address' },
  'invoice.phone': { hu: 'telefonszám', en: 'phone number' },
  'invoice.country': { hu: 'számlázási cím', en: 'billing address' },
  'invoice.state': { hu: 'számlázási cím', en: 'billing address' },
  'invoice.city': { hu: 'számlázási cím', en: 'billing address' },
  'invoice.zip': { hu: 'számlázási cím', en: 'billing address' },
  'invoice.address': { hu: 'számlázási cím', en: 'billing address' },
  'invoice.address2': { hu: 'számlázási cím', en: 'billing address' },
}

export function noticeData(lang: 'hu' | 'en'): string {
  const phrases: string[] = []
  for (const words of Object.values(NOTICE_FIELDS)) {
    if (!phrases.includes(words[lang])) phrases.push(words[lang])
  }
  return phrases.join(', ')
}

/** SP doc §8, the two texts, brackets filled. The link is the last thing. */
export function noticeText(lang: 'hu' | 'en'): { body: string; link: string } {
  if (lang === 'en') {
    return {
      body:
        `I acknowledge the following personal data stored in the user account of the data controller ${MERCHANT_NAME} (${MERCHANT_SEAT}) ` +
        `in the user database of ${ACCEPTANCE_SITE} will be handed over to SimplePay Plc. and is trusted as data processor. ` +
        `The data transferred by the data controller are the following: ${noticeData('en')}. ` +
        'The nature and purpose of the data processing activity performed by the data processor in the SimplePay Privacy Policy can be found at the following link: ',
      link: SIMPLEPAY_PRIVACY_URL,
    }
  }
  return {
    body:
      `Tudomásul veszem, hogy a(z) ${MERCHANT_NAME} (${MERCHANT_SEAT}) adatkezelő által a(z) ${ACCEPTANCE_SITE} ` +
      'felhasználói adatbázisában tárolt alábbi személyes adataim átadásra kerülnek a SimplePay Zrt., mint adatfeldolgozó részére. ' +
      `Az adatkezelő által továbbított adatok köre az alábbi: ${noticeData('hu')}. ` +
      'Az adatfeldolgozó által végzett adatfeldolgozási tevékenység jellege és célja a SimplePay Adatkezelési tájékoztatóban, az alábbi linken tekinthető meg: ',
    link: SIMPLEPAY_PRIVACY_URL,
  }
}
